import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteDetailsV3 } from "./RouteDetailsV3";
import { InspectorHeader } from "../InspectorHeader";
import { TraversalControls } from "../TraversalControls";
import { JumpToGroupButton } from "../JumpToGroupButton";
import { ConfirmActionModal } from "../ConfirmActionModal";
import { useKeyAction } from "hooks/useKeyAction";
import { isFormControlFocused } from "utils/isFormControlFocused";
import type { RouteInspectorV3Props } from "./types";

export const RouteInspectorV3 = ({
  canEdit,
  selectedGroupLabel,
  groupRoutes,
  selectedRoute,
  groupLvMatch,
  boqPositions,
  onSaveLvMatch,
  groupColor,
  routeGroupOptions,
  onSelectRoute,
  onRenameGroup,
  cableTypeStatus,
  onMarkReviewed,
  onToggleSuspicious,
  onDeleteRoute,
  onDeleteRouteGroup,
  onChangeRouteType,
  isTraversing,
  onStartTraversing,
  onStopTraversing,
  seenIds,
  isAllRoutesMode = false,
  onJumpToGroup,
  isPathEditing,
  isPathEditingDirty,
  isPathEditingSubmitting,
  pathEditingMeters,
  onStartPathEditing,
  onSubmitPathEditing,
  onCancelPathEditing,
  hiddenRouteIds,
  onToggleRoutesVisibility,
}: RouteInspectorV3Props) => {
  const { t } = useTranslation();

  const [pendingDeleteRouteId, setPendingDeleteRouteId] = useState<
    string | null
  >(null);

  const [isDeleteGroupConfirmOpen, setIsDeleteGroupConfirmOpen] =
    useState(false);

  const count = groupRoutes.length;

  const currentRouteIndex = useMemo(
    () =>
      selectedRoute
        ? groupRoutes.findIndex((route) => route.id === selectedRoute.id)
        : -1,
    [groupRoutes, selectedRoute],
  );

  const handlePreviousRoute = useCallback(() => {
    if (currentRouteIndex > 0) {
      onSelectRoute(groupRoutes[currentRouteIndex - 1].id);
    }
  }, [currentRouteIndex, groupRoutes, onSelectRoute]);

  const handleNextRoute = useCallback(() => {
    if (currentRouteIndex < groupRoutes.length - 1) {
      onSelectRoute(groupRoutes[currentRouteIndex + 1].id);
    }
  }, [currentRouteIndex, groupRoutes, onSelectRoute]);

  const handleGoToRouteIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < groupRoutes.length) {
        onSelectRoute(groupRoutes[index].id);
      }
    },
    [groupRoutes, onSelectRoute],
  );

  const handleNextUnseen = useCallback(() => {
    for (let offset = 1; offset < groupRoutes.length; offset++) {
      const index = (currentRouteIndex + offset) % groupRoutes.length;
      if (!seenIds.has(groupRoutes[index].id)) {
        onSelectRoute(groupRoutes[index].id);
        return;
      }
    }
  }, [currentRouteIndex, groupRoutes, seenIds, onSelectRoute]);

  const seenCount = useMemo(
    () => groupRoutes.filter((route) => seenIds.has(route.id)).length,
    [groupRoutes, seenIds],
  );

  const handleDeleteShortcut = useCallback(
    (event: KeyboardEvent) => {
      if (!selectedRoute) return;
      if (isFormControlFocused(event)) return;
      setPendingDeleteRouteId(selectedRoute.id);
    },
    [selectedRoute],
  );

  useKeyAction({
    code: "KeyD",
    callback: handleDeleteShortcut,
    options: {
      isDisabled:
        !canEdit ||
        !isTraversing ||
        !selectedRoute ||
        isPathEditing ||
        pendingDeleteRouteId !== null,
    },
  });

  if (!selectedGroupLabel) {
    return (
      <div className="bg-white p-4 h-full overflow-hidden flex flex-col">
        <p className="text-sm text-gray-700">
          {t("smartSurveyor.groupInspector.emptyState")}
        </p>
        <div className="mt-3 flex-1 bg-blue-50 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="bg-white h-full overflow-hidden flex flex-col">
      <InspectorHeader
        canEdit={canEdit}
        groupName={selectedGroupLabel}
        groupTypeOptions={routeGroupOptions}
        groupColor={groupColor}
        onRenameGroup={onRenameGroup}
        nameStatus={cableTypeStatus}
        onMarkReviewed={onMarkReviewed}
        onToggleSuspicious={onToggleSuspicious}
        countLabel={t("smartSurveyor.routeInspector.routesLabel")}
        count={count}
        lvMatch={groupLvMatch}
        boqPositions={boqPositions}
        onSaveLvMatch={onSaveLvMatch}
        isTraversing={isTraversing}
        onStartTraversing={onStartTraversing}
        onStopTraversing={onStopTraversing}
        isGroupNameEditable={!isAllRoutesMode}
        isLvMatchVisible={!isAllRoutesMode}
        onDeleteGroup={
          isAllRoutesMode || !onDeleteRouteGroup
            ? undefined
            : () => setIsDeleteGroupConfirmOpen(true)
        }
      />
      {isTraversing && (
        <div className="overflow-y-auto flex flex-col min-h-0 w-full flex-1 px-4 py-3 gap-3">
          {count > 0 && (
            <TraversalControls
              editingLabel={t("smartSurveyor.routeInspector.editingRoute")}
              selectedIndex={currentRouteIndex}
              total={count}
              seenCount={seenCount}
              onPrev={handlePreviousRoute}
              onNext={handleNextRoute}
              onSelectIndex={handleGoToRouteIndex}
              onNextUnseen={handleNextUnseen}
              isAllMode={isAllRoutesMode}
            />
          )}
          {isAllRoutesMode && selectedRoute && onJumpToGroup && (
            <JumpToGroupButton
              groupName={selectedRoute.cable_type}
              onClick={onJumpToGroup}
            />
          )}
          {selectedRoute ? (
            <RouteDetailsV3
              key={selectedRoute.id}
              canEdit={canEdit}
              route={selectedRoute}
              routeGroupOptions={routeGroupOptions}
              onChangeRouteType={(newCableType) =>
                onChangeRouteType(selectedRoute.id, newCableType)
              }
              onDelete={() => setPendingDeleteRouteId(selectedRoute.id)}
              isPathEditing={isPathEditing}
              isPathEditingDirty={isPathEditingDirty}
              isPathEditingSubmitting={isPathEditingSubmitting}
              pathEditingMeters={pathEditingMeters}
              onStartPathEditing={onStartPathEditing}
              onSubmitPathEditing={onSubmitPathEditing}
              onCancelPathEditing={onCancelPathEditing}
              isRouteHidden={hiddenRouteIds.has(selectedRoute.id)}
              onToggleRouteVisibility={() =>
                onToggleRoutesVisibility([selectedRoute.id])
              }
            />
          ) : (
            <div className="flex items-center justify-center flex-1 text-sm text-gray-400 p-4">
              {t("smartSurveyor.groupInspector.noElementSelected")}
            </div>
          )}
        </div>
      )}
      <ConfirmActionModal
        isOpen={!!pendingDeleteRouteId}
        onClose={() => setPendingDeleteRouteId(null)}
        onConfirm={() => {
          if (pendingDeleteRouteId) {
            onDeleteRoute(pendingDeleteRouteId);
            setPendingDeleteRouteId(null);
          }
        }}
        action="delete"
        title={t("smartSurveyor.routeInspector.deleteRouteModal.title")}
        description={t(
          "smartSurveyor.routeInspector.deleteRouteModal.description",
        )}
      />
      <ConfirmActionModal
        isOpen={isDeleteGroupConfirmOpen}
        onClose={() => setIsDeleteGroupConfirmOpen(false)}
        onConfirm={() => onDeleteRouteGroup?.()}
        action="delete"
        title={t("smartSurveyor.groupInspector.deleteGroupModal.title")}
        description={t(
          "smartSurveyor.groupInspector.deleteGroupModal.routeDescription",
        )}
      />
    </div>
  );
};
